import {EditorSession} from 'substance'

import {loadConfiguration, assertConfiguration, activePlugins} from '../lib/loaders/ConfigurationLoader'
import {loadArticle} from '../lib/loaders/ArticleLoader'
import {PluginLoader} from '../lib/loaders/PluginLoader'
import {SaveHandler} from '../lib/handlers/SaveHandler'
import {StateHandler} from '../lib/handlers/StateHandler'

import {Api} from '../api/Api'
import {Event} from '../lib/constants/Event'

import {DWBasePackage} from './components/base/DWBasePackage'
import {IdfPackage} from './components/idf/IdfPackage'
import {DevToolsPackage} from './components/devtools/DevtoolsPackage'
import {ReadModePackage} from './components/readmode/ReadModePackage'
import {DialogPackage} from './components/dialog/DialogPackage'
import {TopbarPackage} from './components/topbar/TopbarPackage'
import {KeybindingsPackage} from './components/keybinding/KeybindingPackage'
import {NotificationPackage} from './components/notification/NotificationPackage'

import {DWPluginConfigurations} from './services/DWPluginConfigurations'
import {DWConfigurator} from './services/DWConfigurator'
import {ConceptService} from './services/ConceptService'
import {DWFileManager} from '../lib/files/DWFileManager'
import {DWResourceManager} from '../lib/helpers/document/DWResourceManager'
import {idGenerator} from '../lib/utils/IdGenerator'

/**
 * Writer bootstrap
 *
 * @type {Object}
 */
const DWBootstrap = {

    /**
     * Bootstrap main function, creates and inialize all the things
     *
     * @param  {string} id Article id to open
     * @param  {Function} setTotalResources Callback to set total progress steps
     * @param  {Function} onResourceLoaded Callback to increment progress step
     * @return {Promise}
     */
    load({id, setTotalResources, onResourceLoaded}) {
        return new Promise(async (resolve, reject) => {
            try {
                const [article, configuration] = await this.articleAndConfiguration({id})
                const configurator = this.configurator({configuration})

                configurator.addEditorOption({key: 'forcePlainTextPaste', value: true})

                const api = this.api({
                    configurator,
                    article
                })

                api.stateHandler = new StateHandler({api})

                this.handleLoadedArticle({api, id, article})

                const conceptService = this.conceptService({
                    api,
                    configurator
                })

                setTotalResources(Math.round(activePlugins(configuration)) + 2)

                this.packages({
                    configurator,
                    api,
                    conceptService
                })

                await this.plugins({
                    configurator,
                    onLoad: () => {
                        onResourceLoaded()
                    }
                })

                configurator.importAfterPackages()

                this.editorSession({
                    configurator,
                    article,
                    api
                })

                resolve({
                    article,
                    configurator,
                    api,
                    conceptService
                })
            } catch (e) {
                reject(e)
            }
        })
    },

    /**
     * Refreshes editorSession, newsItem, optional etag,
     * happens when article is replaced
     *
     * @param {Api} api - Current api
     * @param {DWConfigurator} configurator - Current configurator
     * @param {string} [etag] - Etag, omit when temp-article
     * @param {string} sourceXml - Raw xml text-data
     *
     * @returns {{newsItem: Document}}
     */
    refresh({api, configurator, etag, sourceXml}) {

        // TODO: Handle api.stateHandler

        const parser = new DOMParser()
        const newsItem = parser.parseFromString(sourceXml, 'application/xml')

        const article = {
            sourceXml,
            newsItem,
            headers: {
                etag
            }
        }

        api.editorSession.dispose()

        this.editorSession({configurator, article, api})

        if (etag) {
            api.router.setEtag(api.newsItem.getGuid(), etag)
        }

        return {
            newsItem
        }
    },

    /**
     * Load article specified by id
     *
     * @param  {string} id
     * @return {Promise}
     */
    articleAndConfiguration({id}) {
        if (id) {
            return Promise.all([
                loadArticle(id),
                loadConfiguration().then(assertConfiguration)
            ])
        }
        else {
            return loadConfiguration()
                .then(assertConfiguration)
                .then(configuration => {
                    return Promise.all([
                        loadArticle(id || configuration.newsItemTemplateId),
                        configuration
                    ])
                })
        }
    },

    /**
     * Preprocess loaded article, for example verifying eTag and cleaning template data
     *
     * @param  {object} api
     * @param  {string} id
     * @param  {string} article
     */
    handleLoadedArticle({api, id, article}) {
        api.router.handleEtag(article, id)

        // If we're loading the article template, guid reference and documentURI
        if (!id) {
            api.newsItem.setGuid(null)
            api.newsItem.removeDocumentURI()
            api.newsItem.setTemporaryId('__temp__' + idGenerator())
        }
    },

    /**
     * Create and initialize Configurator with current configuration
     *
     * @param  {object} configuration Full configuration object structure
     * @return {DWConfigurator}
     */
    configurator({configuration}) {
        const configurator = new DWConfigurator()
        configurator.setConfiguration(configuration)

        return configurator
    },

    /**
     * Create and initialize plugin API
     *
     * @param  {Object} article Article with idf xml and newsItem dom strucure
     * @param  {DWConfigurator} configurator Initalized DWConfigurator
     * @return {Api}
     */
    api({article, configurator}) {
        const pluginConfigurations = configurator.getWriterConfigValue('plugins')

        return new Api({
            newsItem: article.newsItem,
            configurator: configurator,
            pluginConfigurations: new DWPluginConfigurations(pluginConfigurations)
        })
    },

    /**
     * Create and inialize ConceptService
     *
     * @param  {Api} api Initalized api
     * @param  {Object} configuration Full configuration object structure
     * @return {ConceptService}
     */
    conceptService({api, configurator}) {
        const conceptServiceConfig = configurator.getWriterConfigValue('conceptServiceConfig')

        return new ConceptService(api, conceptServiceConfig)
    },

    /**
     * Import all packages needed to startup UI etc
     * @todo Rethink if this is the right place in regards to DWBasePackage
     *
     * @param  {DWConfigurator} configurator Initalized DWConfigurator
     * @param  {Api} api Initalized api
     * @param  {ConceptService} conceptService Initalized ConceptService
     */
    packages({configurator, api, conceptService}) {
        configurator.import(DWBasePackage, {
            api: api,
            conceptService: conceptService
        })

        configurator.import(NotificationPackage)
        configurator.import(TopbarPackage)
        configurator.import(IdfPackage)
        configurator.import(DialogPackage)
        configurator.import(KeybindingsPackage)
        configurator.import(DevToolsPackage)
        configurator.import(ReadModePackage)
    },

    /**
     * Fetch and load all external plugins
     *
     * @param  {DWConfigurator} configurator Initalized DWConfigurator
     * @param  {Function}  onLoad Callback for each loaded plugin
     * @return {Promise}
     */
    async plugins({configurator, onLoad}) {
        const pluginLoader = PluginLoader({
            configurator: configurator,
            pluginLoaded: () => {
                onLoad()
            }
        })

        return await pluginLoader.load()
    },

    /**
     * Create and initalize Substance EditorSession
     *
     * @param  {DWConfigurator} configurator Initalized DWConfigurator
     * @param  {Object} article Article with idf xml and newsItem dom strucure
     * @param  {Api} api Initalized Api
     */
    editorSession({configurator, article, api}) {
        const idfImporter = configurator.createImporter('idf', {
            api: api
        })
        const idfDocument = idfImporter.importDocument(article.sourceXml)

        api.editorSession = new EditorSession(
            idfDocument,
            {
                configurator,
                context: {
                    api
                },
                lang: api.newsItem.getLocale()
            }
        )

        api.newsItemArticle = article.newsItem
        api.editorSession.saveHandler = new SaveHandler({
            configurator,
            api
        })

        api.editorSession.fileManager = new DWFileManager(
            api.editorSession,
            configurator.getFileAdapters(),
            api.editorSession._context
        )

        api.setResourceManager(new DWResourceManager(api.editorSession))

        // Listen for updates in the document/editorSession.
        // Ignore updates without _change or where the update is not
        // stored in the undo history.
        api.editorSession.onUpdate(data => {
            if (!data._change || data._info.history === false) {
                return
            }

            api.events.trigger(null, Event.DOCUMENT_CHANGED, {
                type: 'edit',
                action: 'edit',
                data: data._change
            })
        })
    }

}

export {DWBootstrap}
